import type { SelectOption } from '../../../components/SelectCustom'
import SelectCustom from '../../../components/SelectCustom'
import { CustomerAreaSurfaceCard } from '../customer-area'

type SupportContext = {
  orderId: string
  orderNumber: string
  statusLabel: string
  productHint: string
}

type CustomerSupportRequestCardProps = {
  title: string
  subtitle: string
  supportContext: SupportContext | null
  contextLinkedOrderText: string
  contextOrderStatusText: string
  contextMainProductText: string
  subjectLabel: string
  subjectPlaceholder: string
  categoryLabel: string
  categoryOptions: SelectOption[]
  priorityLabel: string
  priorityOptions: SelectOption[]
  orderIdLabel: string
  orderIdPlaceholder: string
  messageLabel: string
  messagePlaceholder: string
  submitLabel: string
  submittingLabel: string
  supportSubject: string
  supportMessage: string
  supportOrderId: string
  supportCategory: string
  supportPriority: string
  supportSubmitting: boolean
  supportSuccess: string
  onSupportSubjectChange: (value: string) => void
  onSupportMessageChange: (value: string) => void
  onSupportOrderIdChange: (value: string) => void
  onSupportCategoryChange: (value: string) => void
  onSupportPriorityChange: (value: string) => void
  onSubmitSupportRequest: () => void
}

export const CustomerSupportRequestCard = ({
  title,
  subtitle,
  supportContext,
  contextLinkedOrderText,
  contextOrderStatusText,
  contextMainProductText,
  subjectLabel,
  subjectPlaceholder,
  categoryLabel,
  categoryOptions,
  priorityLabel,
  priorityOptions,
  orderIdLabel,
  orderIdPlaceholder,
  messageLabel,
  messagePlaceholder,
  submitLabel,
  submittingLabel,
  supportSubject,
  supportMessage,
  supportOrderId,
  supportCategory,
  supportPriority,
  supportSubmitting,
  supportSuccess,
  onSupportSubjectChange,
  onSupportMessageChange,
  onSupportOrderIdChange,
  onSupportCategoryChange,
  onSupportPriorityChange,
  onSubmitSupportRequest,
}: CustomerSupportRequestCardProps) => (
  <CustomerAreaSurfaceCard title={title} subtitle={subtitle}>
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault()
        onSubmitSupportRequest()
      }}
    >
      {supportContext ? (
        <div className="ca-theme-transition rounded-lg border border-(--color-border-default) bg-(--color-bg-card) px-3 py-2 text-xs text-(--color-text-secondary)">
          <p className="font-semibold text-(--color-text-primary)">{contextLinkedOrderText}</p>
          {supportContext.statusLabel ? <p className="mt-0.5">{contextOrderStatusText}</p> : null}
          {supportContext.productHint ? <p className="mt-0.5">{contextMainProductText}</p> : null}
        </div>
      ) : null}

      <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
        {subjectLabel}
        <input
          value={supportSubject}
          onChange={(event) => onSupportSubjectChange(event.target.value)}
          placeholder={subjectPlaceholder}
          required
          minLength={3}
          maxLength={180}
          className="ca-input h-10 px-3 text-sm"
        />
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
          {categoryLabel}
          <SelectCustom
            value={supportCategory}
            onChange={onSupportCategoryChange}
            ariaLabel={categoryLabel}
            options={categoryOptions}
            size="sm"
            buttonClassName="h-10 rounded-[10px] border-(--color-border-strong) bg-(--color-bg-card) text-sm text-(--color-text-primary)"
          />
        </label>
        <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
          {priorityLabel}
          <SelectCustom
            value={supportPriority}
            onChange={onSupportPriorityChange}
            ariaLabel={priorityLabel}
            options={priorityOptions}
            size="sm"
            buttonClassName="h-10 rounded-[10px] border-(--color-border-strong) bg-(--color-bg-card) text-sm text-(--color-text-primary)"
          />
        </label>
      </div>

      <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
        {orderIdLabel}
        <input
          value={supportOrderId}
          onChange={(event) => onSupportOrderIdChange(event.target.value)}
          placeholder={orderIdPlaceholder}
          className="ca-input h-10 px-3 text-sm"
        />
      </label>

      <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
        {messageLabel}
        <textarea
          value={supportMessage}
          onChange={(event) => onSupportMessageChange(event.target.value)}
          placeholder={messagePlaceholder}
          required
          minLength={10}
          maxLength={5000}
          rows={6}
          className="ca-input px-3 py-2 text-sm"
        />
      </label>

      <div className="flex flex-wrap items-center gap-2.5">
        <button
          type="submit"
          disabled={supportSubmitting}
          className="ca-btn-primary inline-flex h-9 items-center px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
        >
          {supportSubmitting ? submittingLabel : submitLabel}
        </button>
        {supportSuccess ? (
          <span className="text-sm font-medium text-(--color-status-success)">{supportSuccess}</span>
        ) : null}
      </div>
    </form>
  </CustomerAreaSurfaceCard>
)
